import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ShieldAlert } from 'lucide-react';
import api from '@/lib/api';
import { getCompanySettings, invalidateConfigCache } from '@/lib/configCache';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores';

/**
 * Strict Fund Guard toggle (Super Admin only).
 *
 * When ON, the backend blocks any cash/bank Expense or Bill payment whose
 * amount exceeds the paying branch's available balance (reuses the existing
 * branch cash_in_hand / bank_in_hand logic). Persisted on company_settings.
 */
export default function StrictFundGuardCard() {
  const user = useAuthStore((s) => s.user);
  const isSuper = user?.is_super_admin || user?.role === 'admin';
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCompanySettings();
        if (alive) setEnabled(!!data?.strict_fund_guard);
      } catch { /* keep default off */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (!isSuper) return null;

  const toggle = async (next) => {
    setSaving(true);
    setEnabled(next);
    try {
      await api.post('/settings/company', { strict_fund_guard: next });
      invalidateConfigCache('company_settings');
      toast.success(next ? 'Strict Fund Guard enabled' : 'Strict Fund Guard disabled');
    } catch {
      setEnabled(!next);
      toast.error('Failed to update Strict Fund Guard');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-amber-200 dark:border-amber-900" data-testid="strict-fund-guard-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert size={18} className="text-amber-600" /> Strict Fund Guard
        </CardTitle>
        <CardDescription>
          Block any cash or bank Expense / Bill payment that exceeds the paying branch's
          available balance. Credit and online payments are never blocked. Super Admin only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-xl border border-border p-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Enforce available-balance limit</p>
            <p className="text-xs text-muted-foreground">
              {enabled ? 'ON — overspending cash/bank is blocked.' : 'OFF — no fund limit is enforced.'}
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={toggle}
            disabled={loading || saving}
            data-testid="strict-fund-guard-toggle"
          />
        </div>
      </CardContent>
    </Card>
  );
}
